import { createApiClient } from '@aimsos/api-client';
import { tokenService, clearAuthAndRedirect } from '@aimsos/auth';

/**
 * Lazily-constructed HTTP client for the Dashboards app.
 *
 * The client is built on first property access rather than at module load, so
 * the shell has already injected `window.__AIMS_SHELL__` by the time we read
 * the base URL. Mirrors `aims-os-agentic-studio/src/services/apiClient.js`.
 */
function lazyClient(urlKey) {
  let instance = null;
  return new Proxy(
    {},
    {
      get(_, prop) {
        if (!instance) {
          const shell = window.__AIMS_SHELL__ || {};
          // Shell first, then this app's own env: embedded is the normal case,
          // but `pnpm dev --filter @aimsos/dashboards` has no shell to read.
          const fallbackEnv = urlKey === 'apiBaseUrlDS'
            ? import.meta.env.VITE_API_BASE_URL_DS
            : import.meta.env.VITE_API_BASE_URL_DASHBOARD;
          const baseURL = shell[urlKey] || fallbackEnv || '';
          instance = createApiClient({
            baseURL: baseURL.replace(/\/+$/, ''),
            getToken: () => tokenService.getToken(),
            refreshToken: () => tokenService.refreshToken(),
            onAuthFailure: clearAuthAndRedirect,
          }).client;
        }
        const value = instance[prop];
        return typeof value === 'function' ? value.bind(instance) : value;
      },
    },
  );
}

/**
 * HTTP client for the Dashboard API — the gateway that serves the widget
 * module (`{stage}-aimsos-dashboard-api`).
 *
 * Its own gateway, not the platform one: the widget endpoints live under
 * `/v0/dashboard/widget/*` on a separate API, so the shell injects
 * `apiBaseUrlDashboard` from `VITE_API_BASE_URL_DASHBOARD` — see
 * `WidgetsEmbed` / `DashboardsEmbed`. Standalone dev falls back to the app's
 * own env var so the app runs outside the shell too.
 */
export const apiDashboards = lazyClient('apiBaseUrlDashboard');

/** Path prefix every widget resource shares. */
export const WIDGET_API_PREFIX = '/v0/dashboard/widget';

/**
 * HTTP client for the Data Studio API.
 *
 * A third gateway: the Widget Builder's entity and field pickers are served by
 * the data-model aggregate (`/v0/datamodel/...`), which lives with Data Studio
 * rather than with the Dashboard API. Injected as `apiBaseUrlDS`, the same key
 * `DataStudioEmbed` already uses.
 */
export const apiDataStudio = lazyClient('apiBaseUrlDS');

/** Path prefix the data-model resources share. */
export const DATAMODEL_API_PREFIX = '/v0/datamodel';

/**
 * Path prefix every dashboard resource shares. Same aggregate as the widgets,
 * different module — `dashboard.dashboard` rather than `dashboard.widget`.
 */
export const DASHBOARD_API_PREFIX = '/v0/dashboard/dashboard';
